/**
 * Centralized query key factory for TanStack Query cache keys.
 * Use these instead of inline strings to keep cache invalidation reliable.
 */
export const authKeys = {
  all: ["auth"] as const,
  me: () => [...authKeys.all, "me"] as const,
};

export const publisherKeys = {
  all: ["publishers"] as const,
  me: () => [...publisherKeys.all, "me"] as const,
  detail: (id: string) => [...publisherKeys.all, "detail", id] as const,
};

export const adminPublisherKeys = {
  all: ["admin", "publishers"] as const,
  list: (status: string, cursor?: string, q?: string) =>
    [...adminPublisherKeys.all, status, cursor, q] as const,
  allPublishers: (cursor?: string, q?: string) =>
    [...adminPublisherKeys.all, "all", cursor, q] as const,
  pending: (cursor?: string, q?: string) =>
    [...adminPublisherKeys.all, "pending", cursor, q] as const,
  detail: (id: string) => [...adminPublisherKeys.all, "detail", id] as const,
};

export const legalKeys = {
  all: ["legal"] as const,
  currentDoc: (type: string, lang: string) =>
    [...legalKeys.all, "current", type, lang] as const,
};

export const adminLegalKeys = {
  all: ["admin", "legal"] as const,
  list: (docType?: string, language?: string, status?: string, cursor?: string) =>
    [...adminLegalKeys.all, "list", docType, language, status, cursor] as const,
  detail: (id: string) => [...adminLegalKeys.all, "detail", id] as const,
} as const;

export const notificationKeys = {
  all: ["notifications"] as const,
  list: () => [...notificationKeys.all, "list"] as const,
  inbox: () => [...notificationKeys.all, "inbox"] as const,
  hasUnread: () => [...notificationKeys.all, "hasUnread"] as const,
  devices: () => [...notificationKeys.all, "devices"] as const,
};

export const settingsKeys = {
  all: ["settings"] as const,
  preferences: () => [...settingsKeys.all, "preferences"] as const,
};

export const billingKeys = {
  all: ["billing"] as const,
  plans: () => [...billingKeys.all, "plans"] as const,
  subscription: () => [...billingKeys.all, "subscription"] as const,
} as const;

export const titlesKeys = {
  all:    ["titles"] as const,
  list:   (status?: string, cursor?: string, q?: string) =>
           [...titlesKeys.all, "list", status, cursor, q] as const,
  revisions: (cursor?: string) =>
           [...titlesKeys.all, "revisions", cursor] as const,
  revision: (id: string) =>
           [...titlesKeys.all, "revision", id] as const,
  detail: (id: string) =>
           [...titlesKeys.all, "detail", id] as const,
};

export const adminTitlesKeys = {
  all:    ["admin", "titles"] as const,
  list:   (status?: string, publisherId?: string, cursor?: string, q?: string) =>
           [...adminTitlesKeys.all, "list", status, publisherId, cursor, q] as const,
  detail: (id: string) =>
           [...adminTitlesKeys.all, "detail", id] as const,
} as const;

export const revisionReviewKeys = {
  all:    ["admin", "revisions"] as const,
  queue:  (q?: string) => [...revisionReviewKeys.all, "queue", q] as const,
  detail: (titleId: string) => [...revisionReviewKeys.all, "detail", titleId] as const,
} as const;

export const adminDashboardKeys = {
  all:           ["admin", "dashboard"] as const,
  activityChart: (range: string) => [...adminDashboardKeys.all, "activity-chart", range] as const,
  summary:       () => [...adminDashboardKeys.all, "summary"] as const,
} as const;

export const publisherDashboardKeys = {
  all:     ["publisher", "dashboard"] as const,
  summary: () => [...publisherDashboardKeys.all, "summary"] as const,
} as const;

export const adminExportKeys = {
  all: ["admin", "exports"] as const,
  job: (id: string) => [...adminExportKeys.all, id] as const,
} as const;

export const countriesKeys = {
  all: ["countries"] as const,
  list: () => [...countriesKeys.all, "list"] as const,
} as const;

export const enumKeys = {
  all: ["enums"] as const,
  /** Localized — the active i18n language is part of the key so labels refetch on language switch. */
  catalog: (lang: string) => [...enumKeys.all, "catalog", lang] as const,
} as const;

export const serviceKeys = {
  all:  ["services"] as const,
  list: (cursor?: string) => [...serviceKeys.all, "list", cursor] as const,
} as const;

export const adminServiceKeys = {
  all:  ["admin", "services"] as const,
  list: (status?: string, cursor?: string) =>
          [...adminServiceKeys.all, "list", status, cursor] as const,
} as const;